import type { FormEvent, RefObject, MouseEvent as ReactMouseEvent } from 'react'
import type {
  SunoPrompt,
  SunoPromptFilters,
  SunoPromptGroup,
  SunoPromptPool
} from '../../../api/suno'
import type { PlayableTrack } from '../../audio/useAudioPlayer'
import {
  PromptLibraryDesktopControls,
  PromptLibraryList,
  PromptLibraryMobileControls
} from './PromptManagement'
import { type PromptActionMenuPosition } from './PromptManagementItems'
import { createPromptLibraryRenderers, type PromptLibraryItemActions } from './PromptLibraryRenderers'

type PromptLibraryListSectionProps = {
  actionMenuId: string | null
  actionMenuPosition: PromptActionMenuPosition | null
  audioLoading: boolean
  audioPlaying: boolean
  detailsRef: RefObject<HTMLDetailsElement | null>
  error: string | null
  expandedPromptPoolAnchorPromptIds: Record<string, string | undefined>
  expandedPromptPoolIds: Set<string>
  groups: SunoPromptGroup[]
  isLoading: boolean
  items: SunoPrompt[]
  loadingPromptId: string | null
  onClearSearch: () => void
  onCloseActionMenu: () => void
  onCopyText: (value: string, label: string) => Promise<void>
  onDeletePrompt: (prompt: SunoPrompt) => void
  onDissolvePromptPool: (poolId: string) => Promise<void>
  onEditPrompt: (prompt: SunoPrompt) => void
  onGroupChange: (groupId: string) => void
  onNextPage: () => void
  onOpenPromptPoolEditor: (poolId: string) => Promise<void>
  onOpenSimilarity: (prompt: SunoPrompt) => void
  onOpenTracks: (prompt: SunoPrompt) => void
  onPageSizeChange: (pageSize: number) => void
  onPlayPromptTracks: (prompt: SunoPrompt) => Promise<void>
  onPreviousPage: () => void
  onRemovePromptPoolVariant: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onSearchChange: (value: string) => void
  onSetPromptPoolCanonical: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onSort: (sortBy: NonNullable<SunoPromptFilters['sortBy']>) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onToggleActionMenu: (event: ReactMouseEvent<HTMLButtonElement>, menuId: string) => void
  onTogglePromptPoolExpansion: (poolId: string, anchorPromptId: string) => void
  page: number
  pageSize: number
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playingTrack: PlayableTrack | null
  poolDetailsById: Record<string, SunoPromptPool | undefined>
  poolErrorsById: Record<string, string | undefined>
  search: string
  selectedGroupId: string
  sortBy: SunoPromptFilters['sortBy']
  sortDirection: SunoPromptFilters['sortDirection']
  total: number
}

export const PromptLibraryListSection = ({
  actionMenuId,
  actionMenuPosition,
  audioLoading,
  audioPlaying,
  detailsRef,
  error,
  expandedPromptPoolAnchorPromptIds,
  expandedPromptPoolIds,
  groups,
  isLoading,
  items,
  loadingPromptId,
  onClearSearch,
  onCloseActionMenu,
  onCopyText,
  onDeletePrompt,
  onDissolvePromptPool,
  onEditPrompt,
  onGroupChange,
  onNextPage,
  onOpenPromptPoolEditor,
  onOpenSimilarity,
  onOpenTracks,
  onPageSizeChange,
  onPlayPromptTracks,
  onPreviousPage,
  onRemovePromptPoolVariant,
  onSearchChange,
  onSetPromptPoolCanonical,
  onSort,
  onSubmit,
  onToggleActionMenu,
  onTogglePromptPoolExpansion,
  page,
  pageSize,
  playbackPromptId,
  playbackTracks,
  playingTrack,
  poolDetailsById,
  poolErrorsById,
  search,
  selectedGroupId,
  sortBy,
  sortDirection,
  total
}: PromptLibraryListSectionProps) => {
  const itemActions: PromptLibraryItemActions = {
    onCopyText,
    onDeletePrompt,
    onDissolvePromptPool,
    onEditPrompt,
    onOpenPromptPoolEditor,
    onOpenSimilarity,
    onOpenTracks,
    onPlayPromptTracks,
    onRemovePromptPoolVariant,
    onSetPromptPoolCanonical,
    onTogglePromptPoolExpansion
  }

  const playbackState = {
    audioLoading,
    audioPlaying,
    loadingPromptId,
    playbackPromptId,
    playbackTracks,
    playingTrack
  }

  const { renderMobileCard, renderTableRow } = createPromptLibraryRenderers({
    actionMenuId,
    actionMenuPosition,
    expandedPromptPoolAnchorPromptIds,
    expandedPromptPoolIds,
    itemActions,
    onCloseActionMenu,
    onToggleActionMenu,
    playbackState
  })

  return (
    <>
      <PromptLibraryDesktopControls
        detailsRef={detailsRef}
        groups={groups}
        isLoading={isLoading}
        onClearSearch={onClearSearch}
        onGroupChange={onGroupChange}
        onSearchChange={onSearchChange}
        onSubmit={onSubmit}
        search={search}
        selectedGroupId={selectedGroupId}
      />

      <PromptLibraryList
        error={error}
        expandedPromptPoolAnchorPromptIds={expandedPromptPoolAnchorPromptIds}
        expandedPromptPoolIds={expandedPromptPoolIds}
        isLoading={isLoading}
        items={items}
        onNextPage={onNextPage}
        onPageSizeChange={onPageSizeChange}
        onPreviousPage={onPreviousPage}
        onSort={onSort}
        page={page}
        pageSize={pageSize}
        poolDetailsById={poolDetailsById}
        poolErrorsById={poolErrorsById}
        renderMobileCard={renderMobileCard}
        renderTableRow={renderTableRow}
        sortBy={sortBy}
        sortDirection={sortDirection}
        toolbar={(
          <PromptLibraryMobileControls
            groups={groups}
            isLoading={isLoading}
            onGroupChange={onGroupChange}
            onSearchChange={onSearchChange}
            onSubmit={onSubmit}
            search={search}
            selectedGroupId={selectedGroupId}
          />
        )}
        total={total}
      />
    </>
  )
}
